import {
  CountryCodeSelector,
  DEFAULT_COUNTRY_CODE,
  isValidPhoneForSelectedCountry,
} from "@/components/auth/country-code-selector";
import { useForm } from "react-hook-form";
import {
  createUserSchema,
  CreateUserSchema,
  useCreateUser,
  UserRole,
} from "@/lib/api/user.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import { PlusIcon } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import { CheckIcon, ReloadIcon } from "@radix-ui/react-icons";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.tsx";
import { Input } from "@/components/ui/input.tsx";
import { useAbility } from "@/lib/casl/ability-context";
import { hasPrivilegedAccessFromAbility } from "@/lib/casl/privileged-access";

export function AddUser() {
  const ability = useAbility();
  const canCreateUsers =
    hasPrivilegedAccessFromAbility(ability) && ability.can("create", "users");
  const { mutateAsync: createUser, isPending } = useCreateUser();
  const [open, setOpen] = useState(false);
  const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);

  const form = useForm<CreateUserSchema>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
    },
  });

  async function onSubmit(input: CreateUserSchema) {
    const fullPhoneNumber = `${countryCode}${input.phone}`;
    if (!isValidPhoneForSelectedCountry(fullPhoneNumber, countryCode)) {
      form.setError("phone", {
        message: "Please enter a valid phone number",
      });
      return;
    }

    await createUser({ ...input, phone: fullPhoneNumber });

    form.reset();
    setCountryCode(DEFAULT_COUNTRY_CODE);
    setOpen(false);
  }

  if (!canCreateUsers) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          form.reset();
          setCountryCode(DEFAULT_COUNTRY_CODE);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="default"
          className="h-12 rounded-xl bg-buttonBackground-primary px-lg text-buttonText-primary hover:bg-buttonBackground-primary/90"
        >
          <PlusIcon className="mr-sm size-lg" aria-hidden="true" />
          Add user
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add user</DialogTitle>
          <DialogDescription>
            Add the required information to create a new user
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="rounded-xl capitalize">
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem
                          value={UserRole.MENTEE}
                          className="capitalize"
                          disabled
                        >
                          Teen (Disabled) - Self service creation only
                        </SelectItem>
                        <SelectItem
                          value={UserRole.MENTOR}
                          className="capitalize"
                        >
                          Mentor
                        </SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-neutral-500">First Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter first name" {...field} />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-neutral-500">Last Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter last name" {...field} />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-neutral-500">
                    Phone number
                  </FormLabel>
                  <FormControl>
                    <div className="flex items-center gap-2">
                      <CountryCodeSelector
                        value={countryCode}
                        onChange={setCountryCode}
                        className="border-input h-10 rounded-md bg-background"
                      />
                      <Input
                        placeholder="Enter phone"
                        inputMode="tel"
                        type="tel"
                        {...field}
                        onChange={(event) => {
                          field.onChange(
                            event.target.value.replace(/[^0-9]/g, ""),
                          );
                        }}
                      />
                    </div>
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-neutral-500">Email</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter email"
                      inputMode="email"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            <DialogFooter className="gap-2 pt-2 sm:space-x-0">
              <Button disabled={isPending}>
                {isPending ? (
                  <ReloadIcon
                    className="mr-2 size-4 animate-spin"
                    aria-hidden="true"
                  />
                ) : (
                  <CheckIcon className="mr-2 size-4" aria-hidden="true" />
                )}
                Add User
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
